import {useEffect, useState} from 'react';
import withAlerts from '../../lib/withAlerts';
import {Form, Row, Col, Button, Collapse} from 'react-bootstrap';
import {useForm, useWatch} from 'react-hook-form';
import {compose} from '../../lib/compose';
import {useCustomerContext} from '../../lib/context/customerContext';
import {useFeatureContext} from '../../lib/context/featureContext';
import {useAccountMutations} from './accountMutations';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faChevronDown} from '@fortawesome/pro-solid-svg-icons';
import cx from 'classnames';
import ConfirmFeatureToggle from './confirmFeatureToggle';

export const MDMToggle = (props) => {
  const {active, config} = props;
  const {success, error} = props?.alerts;
  const {mdmAccountConfig, featureToggle} = useAccountMutations();
  const customerContext = useCustomerContext();
  const {feature_customer_mdm} = useFeatureContext();
  const {hasPermission} = customerContext;
  const [showConfirm, setShowConfirm] = useState(false);
  const [open, setOpen] = useState(false);
  const {watch, control, unregister, register, reset, getValues, handleSubmit, formState: {errors}, clearErrors} = useForm({
    mode: 'onBlur',
    defaultValues: {
      mdm_lostmode_phone: config?.mdm_lostmode_phone,
      mdm_lostmode_msg: config?.mdm_lostmode_msg,
      mdm_tenant_id: config?.mdm_tenant_id,
      mdm_client_id: config?.mdm_client_id,
      mdm_client_secret: config?.mdm_client_secret,
      feature_customer_mdm_toggle: active?.includes('MDM')
    }
  });

  // RBAC: FEATURE_EDIT has no listed treatment - applying the default rule
  // (FEATURE_VIEW present, which gates the whole Features tab in index.jsx;
  // FEATURE_EDIT absent -> disable the toggle and config fields rather than
  // hide them).
  const featureEditDisabled = !hasPermission('FEATURE_EDIT');

  useEffect(() => {
    if (customerContext) {
      reset({
        mdm_lostmode_phone: customerContext?.mdm_lostmode_phone,
        mdm_lostmode_msg: customerContext?.mdm_lostmode_msg,
        mdm_tenant_id: customerContext?.mdm_tenant_id,
        mdm_client_id: customerContext?.mdm_client_id,
        mdm_client_secret: customerContext?.mdm_client_secret,
      });
    }
  }, [customerContext, reset]);
  
  const hasError = Object.keys(errors)?.length !== 0;
  
  const handleShow = () => setShowConfirm(true);
  const handleClose = () => setShowConfirm(false);

  const handleChange = (v) => {
    if(feature_customer_mdm) {
      setShowConfirm(true);
      return;
    }
    setOpen(true);
    setShowConfirm(false);
  }

  const removeDuplicateValues = (newObject, originalObject) => {
    for (const property in newObject) {
      if (originalObject[property] === newObject[property]) {
          delete newObject[property];
      }
    }
  }

  const disableMdm = async () => {
    const disable = await featureToggle({variables: {
      feature: {
        feature_code: "MDM",
        customer_enabled: false
      }
    }});

    if(disable?.errors) {
      error({msg: 'There was an error disabling the MDM feature. Please try again.'});
    } else {
      //mdm was disabled, ignore the config
      success({msg: 'MDM Integration disabled.'})
    }
  }

  const handleMdmSettings = async (formData) => {
    removeDuplicateValues(formData, customerContext);
    
    const toggle = await featureToggle({variables: {
      feature: {
        feature_code: "MDM",
        customer_enabled: true
      }
    }});

    if(toggle?.errors) {
      error({msg: 'There was an error enabling the MDM feature. Please try again.'});
      return;

    } else {
        
      //update the mdm config with the enabled mdm feature        
      const config = {...formData};
      delete config.feature_customer_mdm_toggle;

      // SECURITY FIX: was customerConfig() - these 5 fields (mdm_tenant_id,
      // mdm_client_id, mdm_client_secret, mdm_lostmode_msg, mdm_lostmode_phone)
      // now go through the FEATURE_EDIT-gated MdmAccountConfig mutation
      // instead of the CONFIG_EDIT-gated CustomerConfig one, matching the
      // backend split (see customer.graphql/customer.js).
      const update = await mdmAccountConfig({variables: {
        config: config
      }});

      if(update?.errors) {
        error({msg: 'There was an error updating the MDM configuration. Please try again.'});
      } else {
        success({msg: 'MDM Enabled and configuration saved.'});
      }
    }
  }
  
  return(
    <Form onSubmit={handleSubmit(handleMdmSettings)}>
      <div className="account-form modal-body">
        <Form.Group>
          <Form.Check
            type="switch"
            id="feature_customer_mdm_toggle"
            disabled={featureEditDisabled}
            onChange={(e) => handleChange(e?.target?.checked)}
            checked={feature_customer_mdm}
            label="MDM Integration"
          />
          <FontAwesomeIcon
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            icon={faChevronDown}
            color="black"
            className="pointer mdm-collapse"
          />
        </Form.Group>
        
        <Collapse in={open}>
          <div className="mt-3">
            <fieldset disabled={featureEditDisabled}>
            <Form.Group className="mb-3">
              <Form.Label className="strong">All fields are required to enable MDM feature.</Form.Label>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="strong">Tenant ID</Form.Label>
              <Form.Control
                {...register('mdm_tenant_id', {
                  required: 'Required when MDM Feature is enabled.'
                })}
                type="text"
                className={cx({'error-input': errors?.mdm_tenant_id})}
                />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="strong">Client ID</Form.Label>
              <Form.Control
                {...register('mdm_client_id', {
                  required: 'Required when MDM Feature is enabled.'
                })}
                type="text"
                className={cx({'error-input': errors?.mdm_client_id})}
                />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="strong">Client Secret</Form.Label>
              <Form.Control
                {...register('mdm_client_secret', {
                  required: 'Required when MDM Feature is enabled.'
                })}
                type="text"
                className={cx({'error-input': errors?.mdm_client_secret})}
                />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="strong">Lost Mode Message</Form.Label>
              <Form.Control
                {...register('mdm_lostmode_msg', {
                  required: 'Required when MDM Feature is enabled.'
                })}
                type="text"
                placeholder="Device locked in Lost Mode. Contact phone number."
                className={cx({'error-input': errors?.mdm_lostmode_msg})}
                />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Lost Mode Phone Number</Form.Label>
              <Form.Control
                {...register('mdm_lostmode_phone', {
                  required: 'Required when MDM Feature is enabled.'
                })}
                type="phone"
                placeholder="8885551234"
                className={cx({'error-input': errors?.mdm_lostmode_phone})}
                />
            </Form.Group>

            <Form.Group>
              {hasError && <strong className="privoro-black">All fields required when MDM Feature is enabled.</strong>}
            </Form.Group>
          
            <div className="d-flex justify-content-end">
              <Button type="submit" disabled={hasError} className="ms-2">{feature_customer_mdm ? 'Submit' : 'Enable'}</Button> 
            </div>
            </fieldset>
          </div>
        </Collapse>
        <ConfirmFeatureToggle
          showconfirm={showConfirm}
          handleclose={handleClose}
          onChange={disableMdm}
          title="MDM"
          message={"MDM feature will be disabled on all SafeCases."}
        />
      </div>
    </Form>
  );
}

export default compose(
  withAlerts
)(MDMToggle);
